import React from "react";
import "./Satochip.css"; // Import the CSS file

function Satochip() {
  return (
    <div className="satochip-container">
      <h1>Satochip Instructions</h1>
      <p>
        Learn how to set up and use the Satochip hardware wallet. Follow the
        detailed steps below to build your own Satochip hardware wallet and load
        the applet onto a smart card.
      </p>
      <p>
        Visit the official documentation:{" "}
        <a
          href="https://satochip.io/build-your-own-satochip-hardware-wallet/"
          target="_blank"
          rel="noopener noreferrer"
          className="satochip-link"
        >
          Build Your Own Satochip Hardware Wallet
        </a>
      </p>
      <ol>
        <li>
          Clone the{" "}
          <a
            href="https://github.com/Toporin/SatochipApplet"
            target="_blank"
            rel="noopener noreferrer"
            className="satochip-link"
          >
            Satochip Applet repository
          </a>{" "}
          repository:
          <div className="code-block">
            <code>git clone git@github.com:Toporin/SatochipApplet.git</code>
          </div>
        </li>
        <li>
          Build the Satochip applet:
          <ul>
            <li>
              Option 1: Download an existing release from the{" "}
              <a
                href="https://github.com/Toporin/SatochipApplet/releases"
                target="_blank"
                rel="noopener noreferrer"
                className="satochip-link"
              >
                Satochip Applet Releases
              </a>
              .
            </li>
            <li>
              Option 2: Follow the steps in the README to create your own build.
              <ul>
                <li>
                  Download the latest release of{" "}
                  <a
                    href="https://github.com/martinpaljak/ant-javacard/releases/latest/download/ant-javacard.jar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="satochip-link"
                  >
                    Ant-Javacard
                  </a>{" "}
                  and place it in the `lib` folder.
                </li>
                <li>
                  Install Apache Ant:
                  <div className="code-block">
                    <code>sudo apt install ant</code>
                  </div>
                </li>
                <li>
                  Use or install the recommended Java JDK (8 or 11):
                  <div className="code-block">
                    <code>sudo update-alternatives --config java</code>
                  </div>
                </li>
                <li>
                  Download the Javacard SDK into the `satochip` project in the
                  `sdks` folder:
                  <div className="code-block">
                    <code>
                      git submodule add
                      https://github.com/martinpaljak/oracle_javacard_sdks sdks
                    </code>
                  </div>
                </li>
                <li>
                  Build the project using Apache Ant:
                  <div className="code-block">
                    <code>ant</code>
                  </div>
                  This will generate the `.cap` file in the `SatochipApplet`
                  folder.
                </li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          Put the <code>.cap</code> file in the same folder as the{" "}
          <code>gp.jar</code> file, which is located in the{" "}
          <code>tool/target</code> directory of the GlobalPlatformPro
          repository.
          <div className="code-block">
            <code>
              mv &lt;path-to-Applet&gt;/SatochipApplet/SatoChip-3.0.4.cap
              &lt;path-to-GP&gt;/GlobalPlatformPro/tool/target/
            </code>
          </div>
          This ensures the `.cap` file is in the correct directory for the
          loading process.
        </li>
        <li>
          Connect an NFC reader to your computer and insert your Java J3R180 NFC
          card.
        </li>
      </ol>

      <h2>Loading the Satochip Applet on the Card</h2>
      <ul>
        <li>
          <strong>Windows:</strong>
          <ul>
            <li>
              List the applets on the card:
              <div className="code-block">
                <code>gp.exe -l</code>
              </div>
            </li>
            <li>
              Load the applet:
              <div className="code-block">
                <code>gp.exe -install .\SatoChip-3.0.4.cap</code>
              </div>
            </li>
            <li>
              Delete the applet:
              <div className="code-block">
                <code>gp.exe -uninstall .\SatoChip-3.0.4.cap</code>
              </div>
            </li>
          </ul>
        </li>
        <li>
          <strong>Ubuntu/Linux:</strong>
          <ul>
            <li>
              List the applets on the card:
              <div className="code-block">
                <code>java -jar gp.jar -l</code>
              </div>
            </li>
            <li>
              Load the applet:
              <div className="code-block">
                <code>java -jar gp.jar -install ./SatoChip-3.0.4.cap</code>
              </div>
            </li>
            <li>
              Delete the applet:
              <div className="code-block">
                <code>java -jar gp.jar -uninstall ./SatoChip-3.0.4.cap</code>
              </div>
            </li>
          </ul>
        </li>
      </ul>

      <h2>Supported Software Clients</h2>
      <p>Satochip is compatible with the following desktop software:</p>
      <ul>
        <li>
          The{" "}
          <a
            href="https://sparrowwallet.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="satochip-link"
          >
            Sparrow Wallet
          </a>{" "}
          supports the Satochip hardware wallet for seamless Bitcoin management
          on desktop.
        </li>
        <li>
          The{" "}
          <a
            href="https://github.com/Toporin/electrum-satochip/releases"
            target="_blank"
            rel="noopener noreferrer"
            className="satochip-link"
          >
            Bitcoin Electrum-Satochip{" "}
          </a>
          is a version of Electrum that was slightly modified to integrate the
          Satochip hardware wallet.
        </li>
      </ul>

      <h2>Using the Card</h2>
      <ul>
        <li>
          Download and install the{" "}
          <a
            href="https://sparrowwallet.com/download/"
            target="_blank"
            rel="noopener noreferrer"
            className="satochip-link"
          >
            Sparrow Wallet
          </a>{" "}
          desktop application.
        </li>

        <li>
          Connect your Satochip hardware wallet to your computer using an NFC
          reader.
        </li>
        <li>
          Follow the on-screen instructions to securely manage and store your
          recovery seeds.           For a visual guide, watch the Satochip video (see video links in the instructions above).
        </li>
        <li>
          Store your recovery seed safely using Seedkeeper. Follow the{" "}
          <a
            href={`${process.env.PUBLIC_URL}/instructions/seedkeeper`}
            className="seedkeeper-link"
          >
            instructions
          </a>{" "}
          on how to create and initialize a SeedKeeper card.
        </li>
      </ul>

      <h2>Mobile Integration</h2>
      <p>
        Satochip is in the process of beeing integrated with{" "}
        <a
          href="https://blockstream.com/green/"
          target="_blank"
          rel="noopener noreferrer"
          className="satochip-link"
        >
          Green Wallet
        </a>{" "}
        for mobile devices.
      </p>
    </div>
  );
}

export default Satochip;
